import React from 'react';
import { useApp } from '../context/AppContext';
import { statusOptions, releaseStatusOptions } from '../mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, AlertCircle, Mic, Send, Users, CalendarClock, ArrowRight, Copy, ExternalLink, MapPin, Sparkles, Video, Search, Loader2, HandCoins, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { Guests as GuestsApi } from '../lib/api';
import axios from 'axios';
import RecentActivityStrip from './RecentActivityStrip';

const StatButton = ({ icon: Icon, label, value, accent, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="text-left transition-all hover:shadow-sm hover:-translate-y-px focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-400 rounded-md"
  >
    <Card className="border-stone-200 hover:border-stone-300 cursor-pointer">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: `${accent}1f`, color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
          <div className="text-2xl font-serif text-stone-900">{value}</div>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-stone-300" />
      </CardContent>
    </Card>
  </button>
);

const LIMIT_OPTIONS = [5, 8, 15, 30, 0];
const useListLimit = (key, fallback = 8) => {
  const [limit, setLimit] = React.useState(() => {
    const v = parseInt(localStorage.getItem(`dash-limit-${key}`), 10);
    return Number.isNaN(v) ? fallback : v;
  });
  const update = (v) => { setLimit(v); localStorage.setItem(`dash-limit-${key}`, String(v)); };
  return [limit, update];
};

const LimitSelect = ({ value, onChange, testId }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(parseInt(e.target.value, 10))}
    className="h-7 text-[11.5px] text-stone-600 bg-white border border-stone-200 rounded px-1.5 focus:outline-none focus:ring-1 focus:ring-stone-300"
    title="How many to show"
  >
    {LIMIT_OPTIONS.map(n => <option key={n} value={n}>{n === 0 ? 'Show all' : `Show ${n}`}</option>)}
  </select>
);

export default function Dashboard({ onSelect, onCompose }) {
  const [recordLimit, setRecordLimit] = useListLimit('record');
  const [completedLimit, setCompletedLimit] = useListLimit('completed');
  const { filteredGuests, guests, activeShow, activeShowId, setActiveTable, setActiveView, setStatusFilter, refreshGuests } = useApp();
  const [bulkFinding, setBulkFinding] = React.useState(false);
  const [supportStats, setSupportStats] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/support/stats`).then(r => setSupportStats(r.data)).catch(() => {});
  }, []);

  const runBulkFindPlatforms = async () => {
    if (bulkFinding) return;
    if (!window.confirm(`Auto-find platform links for every recorded or published guest on ${activeShow.name}?\n\nThis will only fill in missing links (existing ones are preserved). It may use several Serper.dev API calls per guest.`)) return;
    setBulkFinding(true);
    try {
      const r = await GuestsApi.bulkFindPlatforms(activeShowId, true);
      toast({
        title: `Auto-find complete — ${r.updated}/${r.processed} guest${r.processed === 1 ? '' : 's'} updated`,
        description: `Processed ${r.processed}, skipped ${r.skipped} already-complete, ${r.failed} failed.`,
        duration: 10000,
      });
      if (r.updated > 0) await refreshGuests();
    } catch (e) {
      toast({ title: 'Bulk auto-find failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setBulkFinding(false);
    }
  };

  // We need the *raw* show-scoped list (NOT the statusFilter-filtered version)
  // so the dashboard counts are stable while you bounce between filters.
  const showGuests = guests.filter(g => g.showId === activeShowId);

  const total = showGuests.length;
  const pendingReleases = showGuests.filter(g => g.releaseStatus !== 'signed' && g.status !== 'potential').length;
  const upcoming = showGuests.filter(g => ['invited', 'confirmed'].includes(g.status)).length;
  const published = showGuests.filter(g => g.status === 'published').length;

  const allToRecord = showGuests
    .filter(g => g.status === 'confirmed')
    .sort((a, b) => (a.recordingDate || a.episodeDate || '9999').localeCompare(b.recordingDate || b.episodeDate || '9999'));
  const showsToRecord = recordLimit ? allToRecord.slice(0, recordLimit) : allToRecord;
  const allCompleted = showGuests
    .filter(g => ['recorded', 'edited', 'published'].includes(g.status))
    .sort((a, b) => (b.episodeDate || b.recordingDate || b.publishedAt || '').localeCompare(a.episodeDate || a.recordingDate || a.publishedAt || ''));
  const completed = completedLimit ? allCompleted.slice(0, completedLimit) : allCompleted;
  const potentials = showGuests
    .filter(g => g.status === 'potential')
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    .slice(0, 8);

  const goFiltered = (filter) => {
    setStatusFilter(filter);
    setActiveTable('guests');
    setActiveView('grid');
  };

  // Suppress unused warning — filteredGuests is exposed from context but we rely on showGuests here
  void filteredGuests;

  return (
    <div className="p-4 md:p-5 overflow-auto h-full">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-serif text-stone-900 tracking-tight">Welcome back, Paul</h1>
          <p className="text-[13px] text-stone-500">Here is what is happening on {activeShow.name}.</p>
        </div>
        <Button onClick={onCompose} className="text-white w-full sm:w-auto" style={{ background: activeShow.color }}>
          <Send className="w-4 h-4 mr-2" /> Compose email
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatButton testid="stat-total" icon={Users} label="Total guests" value={total} accent={activeShow.color} onClick={() => goFiltered('all')} />
        <StatButton testid="stat-upcoming" icon={CalendarClock} label="Upcoming" value={upcoming} accent="#3B82F6" onClick={() => goFiltered('upcoming')} />
        <StatButton testid="stat-pending" icon={AlertCircle} label="Pending releases" value={pendingReleases} accent="#F59E0B" onClick={() => goFiltered('pending-release')} />
        <StatButton testid="stat-published" icon={Mic} label="Published" value={published} accent="#10B981" onClick={() => goFiltered('published')} />
        <StatButton
          testid="stat-monthly-supporters"
          icon={HandCoins}
          label="Monthly supporters"
          value={supportStats ? <>{supportStats.monthlySupporters} <span className="text-[13px] text-stone-500 font-sans">· ${supportStats.monthlyRecurring}/mo</span></> : '—'}
          accent="#E11D48"
          onClick={() => setActiveTable('contributions')}
        />
      </div>

      <RecentActivityStrip
        showId={activeShowId}
        onOpenGuest={(gid) => onSelect(gid)}
        onOpenProspect={() => setActiveTable('prospects')}
      />

      {potentials.length > 0 && (
        <Card className="border-violet-200 mb-4" data-testid="potential-guests-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" /> Potential guests
              <span className="text-[11px] text-stone-400 font-normal ml-auto">scan locations for upcoming travel</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-2">
            {potentials.map(g => (
              <button
                key={g.id}
                onClick={() => onSelect(g.id)}
                data-testid={`potential-card-${g.id}`}
                className="text-left flex items-center gap-3 p-2.5 rounded-md border border-violet-100 bg-violet-50/30 hover:bg-violet-50"
              >
                <div className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold shrink-0" style={{ background: '#A78BFA' }}>{g.name[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-stone-900 truncate">{g.name}</div>
                  <div className="text-[11.5px] text-stone-500 truncate">{[g.role, g.company].filter(Boolean).join(' · ') || 'No role yet'}</div>
                  {g.location ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-100 rounded-full px-2 py-0.5 max-w-full">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span className="truncate">{g.location}</span>
                    </div>
                  ) : (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-stone-400">
                      <MapPin className="w-3 h-3" /> No location set
                    </div>
                  )}
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="border-stone-200" data-testid="shows-to-record-card">
          <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-y-2 space-y-0">
            <CardTitle className="text-[15px] font-semibold flex items-center gap-2">
              <Video className="w-4 h-4 text-blue-600" /> Shows To Record
              <Badge variant="outline" className="text-[10.5px]" data-testid="shows-to-record-count">{recordLimit && allToRecord.length > recordLimit ? `${showsToRecord.length} of ${allToRecord.length}` : allToRecord.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-1">
              <LimitSelect value={recordLimit} onChange={setRecordLimit} testId="shows-to-record-limit" />
              <Button variant="ghost" size="sm" className="h-7 text-stone-600" onClick={() => goFiltered('upcoming')}>
                See all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 pt-2">
            {showsToRecord.length === 0 && <div className="text-stone-400 text-[13px] py-6 text-center">No confirmed recordings yet.</div>}
            {showsToRecord.map(g => {
              const st = statusOptions.find(o => o.value === g.status);
              return (
                <div key={g.id} className="flex items-center gap-3 p-2.5 rounded-md border border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => onSelect(g.id)}>
                  {g.avatar ? <img src={g.avatar} className="w-9 h-9 rounded-full object-cover" alt="" /> :
                    <div className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold" style={{ background: activeShow.color }}>{g.name[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-stone-900 truncate">{g.name}</div>
                    <div className="text-[11.5px] text-stone-500 truncate">
                      {g.episodeTitle || 'No episode title'} · record {g.recordingDate ? new Date(g.recordingDate).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: g.recordingDate.length > 10 ? '2-digit' : undefined, minute: g.recordingDate.length > 10 ? '2-digit' : undefined }) : 'TBD'}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10.5px] hidden sm:inline-flex" style={{ color: st?.color, borderColor: `${st?.color}55` }}>{st?.label || g.status}</Badge>
                  {g.talkingPoints?.url && (
                    <Button asChild size="sm" variant="ghost" className="h-8 text-red-700 hover:bg-red-50" title="Open talking points full screen" data-testid={`talking-points-quick-${g.id}`} onClick={(e) => e.stopPropagation()}>
                      <a href={`/prep/${g.id}`} target="_blank" rel="noreferrer"><FileText className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Notes</span></a>
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onSelect(g.id); }} className="text-stone-600 h-8 hidden sm:inline-flex">
                    Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="completed-card">
          <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-y-2 space-y-0">
            <CardTitle className="text-[15px] font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Completed
              <Badge variant="outline" className="text-[10.5px]" data-testid="completed-count">{completedLimit && allCompleted.length > completedLimit ? `${completed.length} of ${allCompleted.length}` : allCompleted.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-1">
              <LimitSelect value={completedLimit} onChange={setCompletedLimit} testId="completed-limit" />
              <Button
                data-testid="bulk-find-platforms-btn"
                variant="ghost"
                size="sm"
                className="h-7 text-stone-600"
                onClick={runBulkFindPlatforms}
                disabled={bulkFinding || completed.length === 0}
                title="Auto-find Apple/Spotify/YouTube links for every published guest"
              >
                {bulkFinding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                {bulkFinding ? 'Finding…' : 'Auto-find all'}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-stone-600" onClick={() => goFiltered('completed')}>
                See all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 pt-2">
            {completed.length === 0 && <div className="text-stone-400 text-[13px] py-6 text-center">No completed episodes yet.</div>}
            {completed.map(g => {
              const st = statusOptions.find(o => o.value === g.status);
              const rel = releaseStatusOptions.find(r => r.value === g.releaseStatus);
              return (
                <div key={g.id} className="flex items-center gap-3 p-2.5 rounded-md border border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => onSelect(g.id)}>
                  {g.avatar ? <img src={g.avatar} className="w-9 h-9 rounded-full object-cover" alt="" /> :
                    <div className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold" style={{ background: '#10B981' }}>{g.name[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-stone-900 truncate">{g.name}</div>
                    <div className="text-[11.5px] text-stone-500 truncate">
                      {g.episodeTitle || 'No episode title'} · {g.status === 'published' && (g.episodeDate || g.publishedAt) ? `aired ${new Date(g.episodeDate || g.publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : (rel?.label || 'in progress')}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10.5px] hidden sm:inline-flex" style={{ color: st?.color, borderColor: `${st?.color}55` }}>{st?.label || g.status}</Badge>
                  {g.talkingPoints?.url && (
                    <Button asChild size="sm" variant="ghost" className="h-8 text-red-700 hover:bg-red-50" title="Open talking points full screen" data-testid={`talking-points-quick-${g.id}`} onClick={(e) => e.stopPropagation()}>
                      <a href={`/prep/${g.id}`} target="_blank" rel="noreferrer"><FileText className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Notes</span></a>
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onSelect(g.id); }} className="text-stone-600 h-8 hidden sm:inline-flex">
                    Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-3 border-stone-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] font-semibold">Your booking link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-2">
            <div className="text-[11.5px] text-stone-500">Share this link instead of Calendly. Guests pick a time, the app books it to your Outlook calendar &amp; sends a confirmation.</div>
            <div className="flex items-center gap-2 p-2.5 rounded-md border border-stone-200 bg-stone-50">
              <code className="flex-1 text-[11.5px] text-stone-700 truncate">{`${window.location.origin}/book/${activeShow.id}`}</code>
              <Button size="sm" variant="outline" className="h-7" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book/${activeShow.id}`); toast({ title: 'Copied' }); }}><Copy className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" className="h-7" onClick={() => window.open(`/book/${activeShow.id}`, '_blank')}><ExternalLink className="w-3.5 h-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
